// Tasks — /dashboard/tasks
//
// Lists location tasks, opens an edit drawer with TipTap editor + the
// 7-toggle config + assignment count link.
//
// Backend: GET / POST / PATCH / DELETE /v1/admin/tasks (PR E).
// Editor:  TipTapEditor component (PR F).
// Assignment editor: separate /dashboard/tasks/:id/assignments page (PR G).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, RefreshCw, ServerCrash, Inbox, Building2, Trash2, FileText,
  Link as LinkIcon, ClipboardList, MapPin, ArrowRight, Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import TipTapEditor from "../components/TipTapEditor.js";
import { humanizeRRule } from "../lib/humanRrule.js";
import {
  listTasks, createTask, patchTask, archiveTask, friendlyTasksError,
  type TaskRow, type TaskBody,
} from "../lib/tasksApi.js";
import { listAssignmentsForTask, type AssignmentRow } from "../lib/assignmentsApi.js";
import { listLocations, type LocationRow } from "../lib/locationsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: TaskRow[]; total: number }
  | { status: "error"; message: string };

type EditState = null | { mode: "new" } | { mode: "edit"; row: TaskRow };

export default function Tasks(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:site") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<EditState>(null);
  const pag = usePagination({ urlPrefix: "tasks_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listTasks({ tenantId: tenantParam, status: "active", limit: pag.limit, offset: pag.offset });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlyTasksError(err) });
    }
  }, [tenantParam, pag.limit, pag.offset]);

  useEffect(() => { void load(); }, [load]);

  const columns: Column<TaskRow>[] = useMemo(() => [
    {
      key: "name", header: "任務名稱", primary: true, width: "30%",
      render: (t) => (
        <div className="flex items-start gap-2">
          <ClipboardList className="h-4 w-4 text-text-muted shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-medium text-text-primary truncate">{t.name}</div>
            {t.description_short && (
              <div className="text-2xs text-text-muted truncate">{t.description_short}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "evidence", header: "證據", hideOnMobile: true,
      render: (t) => <ToggleBadges keys={[
        ["text", t.requires_text],
        ["照片", t.requires_camera],
        ["檔案", t.requires_file],
      ]} />,
    },
    {
      key: "verify", header: "驗證",
      render: (t) => <ToggleBadges keys={[
        ["裝置", t.verify_device],
        ["人臉", t.verify_face],
        // 地理位置（geofence）UI 暫時隱藏 — NTAG424 已綁實體位置，
        // geo 驗證冗餘。Backend / DB 欄位保留，之後要開回來只要 uncomment。
        // ["位置", t.verify_geo],
      ]} />,
    },
    {
      key: "sign", header: "簽名",
      render: (t) => t.requires_signature
        ? <span className="badge-success text-2xs">需簽</span>
        : <span className="text-2xs text-text-muted">—</span>,
    },
    {
      key: "assignments", header: "派發",
      render: (t) => (
        <Link
          to={`/dashboard/tasks/${t.id}/assignments`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-2xs text-accent hover:text-accent-hover hover:bg-bg-hover"
        >
          <LinkIcon className="h-3 w-3" />
          {t.assignment_count} 條
        </Link>
      ),
    },
    {
      key: "updated", header: "最後更新", hideOnMobile: true,
      render: (t) => <span className="font-mono text-2xs text-text-secondary">
        {formatTaipeiDateTime(t.updated_at)}
      </span>,
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="任務" description="地點任務定義（圖文說明 + 驗證 + 證據 + 簽名）" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="任務"
        description="地點任務定義（圖文說明 + 驗證 + 證據 + 簽名 toggles）"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="tasks.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>
                <Plus className="h-4 w-4" /> 新增任務
              </button>
            )}
          </>
        }
      />
      {state.status === "loading" && <TasksSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="任務列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={FileText} title="尚未建立任何任務" description="第一次使用？點右上「新增任務」開始建立地點任務定義。"
          action={canWrite ? <button data-tour-id="tasks.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增任務</button> : undefined} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <DataTable
            rows={state.rows}
            columns={columns}
            rowKey={(r) => r.id}
            searchableText={(r) => `${r.name} ${r.description_short ?? ""}`}
            onRowClick={canWrite ? (r) => setEditing({ mode: "edit", row: r }) : undefined}
            serverPagination
          />
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={state.total}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.row.name}` : "新增任務"}
        description="任務名稱、圖文說明、驗證 / 證據 / 簽名 toggles"
        size="lg"
      >
        {editing && (
          <TaskForm
            mode={editing.mode}
            row={editing.mode === "edit" ? editing.row : undefined}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function ToggleBadges({ keys }: { keys: [string, boolean][] }): React.ReactElement {
  const on = keys.filter(([_, v]) => v);
  if (on.length === 0) return <span className="text-2xs text-text-muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {on.map(([k]) => (
        <span key={k} className="text-2xs px-1.5 py-0.5 rounded bg-accent/15 text-accent">{k}</span>
      ))}
    </div>
  );
}

function TaskForm({ mode, row, tenantId, onClose, onSaved, canArchive }: {
  mode: "new" | "edit";
  row?: TaskRow;
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
  canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(row?.name ?? "");
  const [descShort, setDescShort] = useState(row?.description_short ?? "");
  const [doc, setDoc] = useState<string | null>(row?.instruction_doc ?? null);
  const [reqText, setReqText] = useState(row?.requires_text ?? false);
  const [reqCam, setReqCam] = useState(row?.requires_camera ?? false);
  const [reqFile, setReqFile] = useState(row?.requires_file ?? false);
  const [verDev, setVerDev] = useState(row?.verify_device ?? false);
  const [verFace, setVerFace] = useState(row?.verify_face ?? false);
  const [verGeo, setVerGeo] = useState(row?.verify_geo ?? false);
  const [reqSig, setReqSig] = useState(row?.requires_signature ?? false);
  const [allowSkip, setAllowSkip] = useState(row?.allow_skip ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    const body: TaskBody = {
      name: name.trim(),
      description_short: descShort.trim() || null,
      instruction_doc: doc,
      requires_text: reqText,
      requires_camera: reqCam,
      requires_file: reqFile,
      verify_device: verDev,
      verify_face: verFace,
      verify_geo: verGeo,
      requires_signature: reqSig,
      allow_skip: allowSkip,
    };
    try {
      if (mode === "new") await createTask(body, tenantId);
      else if (row) await patchTask(row.id, body, tenantId);
      onSaved();
    } catch (e) { setErr(friendlyTasksError(e)); }
    finally { setBusy(false); }
  };

  const dlg = useConfirmDialog();
  const archive = async () => {
    if (!row) return;
    if (!(await dlg.confirm(`確定要封存任務「${row.name}」？\n\n所有指派也會一併停止。`, { destructive: true, okLabel: "封存" }))) return;
    setBusy(true); setErr(null);
    try { await archiveTask(row.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyTasksError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Field label="任務名稱" required>
        <input data-tour-id="tasks.field.name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="例：機房深夜巡查" />
      </Field>
      <Field label="一句話摘要（可選）" hint="顯示在列表的次標題">
        <input className="input" value={descShort} onChange={(e) => setDescShort(e.target.value)} maxLength={500}
               placeholder="例：每日 22:00–06:00 機房門禁 + 溫度確認" />
      </Field>

      <Field label="任務說明（圖文）" hint="工具列支援標題 / 列表 / 引用 / 圖片 / 連結 / 程式碼">
        <TipTapEditor
          value={doc}
          onChange={setDoc}
          tenantId={tenantId}
          placeholder="這裡輸入任務的詳細指引，巡邏員感應標籤後會看到這段內容。"
        />
      </Field>

      <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
        <legend className="text-sm font-semibold text-text-primary px-2">證據要求</legend>
        <Toggle label="文字回應" sublabel="巡邏員感應後填寫文字說明" value={reqText} onChange={setReqText} />
        <Toggle label="即時相機拍攝（不接受相簿選取）" value={reqCam} onChange={setReqCam} />
        <Toggle label="一般檔案上傳" sublabel="PDF / 文件等" value={reqFile} onChange={setReqFile} />
      </fieldset>

      <VerifyMethodRadio
        verDev={verDev} verFace={verFace}
        onChange={(dev, face) => { setVerDev(dev); setVerFace(face); }}
      />
      {/* 地理位置（geofence）UI 暫時隱藏 — NTAG424 已綁實體位置，
          geo 驗證冗餘。verGeo state + verify_geo 仍隨表單一起送，
          既有 task 的設定值會原樣保留；之後要開回來只要 uncomment。 */}

      <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
        <legend className="text-sm font-semibold text-text-primary px-2">簽名</legend>
        <Toggle label="需要簽名" sublabel="任務最後加一步手寫簽名" value={reqSig} onChange={setReqSig} />
      </fieldset>

      <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
        <legend className="text-sm font-semibold text-text-primary px-2">跳過政策</legend>
        <Toggle label="允許跳過 + 寫原因（寬鬆）" sublabel="關閉則為「嚴格模式」，必須完成所有要求才算" value={allowSkip} onChange={setAllowSkip} />
      </fieldset>

      {/* Assignment summary — gives mobile + desktop a clear entry to
          /tasks/:id/assignments without depending on the row column
          being visible. New-mode shows a hint instead since assignments
          can't exist before the task itself does. */}
      <AssignmentsSection mode={mode} taskId={row?.id ?? null} tenantId={tenantId} />

      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}

      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && row && canArchive && (
            <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}>
              <Trash2 className="h-4 w-4" /> 封存
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="tasks.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !name.trim()}>
            {busy ? "儲存中…" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface AssignmentsSectionProps {
  mode: "new" | "edit";
  taskId: string | null;
  tenantId: string | null;
}

function AssignmentsSection({ mode, taskId, tenantId }: AssignmentsSectionProps): React.ReactElement {
  const [items, setItems] = useState<AssignmentRow[] | null>(null);
  const [cpById, setCpById] = useState<Map<string, LocationRow>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit" || !taskId) { setItems([]); return; }
    let cancelled = false;
    setLoading(true); setErr(null);
    void (async () => {
      try {
        const [a, c] = await Promise.all([
          listAssignmentsForTask(taskId, tenantId),
          listLocations({ tenantId, limit: 500 }).catch(() => ({ items: [] as LocationRow[] })),
        ]);
        if (cancelled) return;
        setItems(a.items);
        setCpById(new Map(c.items.map((x) => [x.id, x])));
      } catch (e) {
        if (!cancelled) setErr((e as Error).message ?? "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, taskId, tenantId]);

  if (mode === "new") {
    return (
      <fieldset className="rounded-md border border-dashed border-border bg-bg-raised/50 p-4">
        <legend className="text-sm font-semibold text-text-primary px-2">派發至地點</legend>
        <p className="text-xs text-text-muted">
          先儲存任務後，這裡會出現「管理派發」按鈕，可指派到一或多個地點，
          並設定 RRULE 排程、目標角色 / 個別巡邏員、有效期間。
        </p>
      </fieldset>
    );
  }

  return (
    <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <legend className="text-sm font-semibold text-text-primary px-2">
          派發至地點{items ? ` · ${items.length} 條` : ""}
        </legend>
        {taskId && (
          <Link
            to={`/dashboard/tasks/${taskId}/assignments`}
            className="text-xs text-accent hover:text-accent-hover inline-flex items-center gap-1"
          >
            管理派發 <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      {loading && <Skeleton className="h-12 w-full" />}
      {err && <p className="text-2xs text-danger">載入失敗：{err}</p>}
      {items && items.length === 0 && !loading && (
        <p className="text-xs text-text-muted">
          此任務尚未派發到任何地點。點上方「管理派發」可新增。
        </p>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-1.5">
          {items.slice(0, 5).map((a) => {
            const cp = cpById.get(a.location_id);
            return (
              <li
                key={a.id}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-bg-base border border-border-subtle"
              >
                <MapPin className="h-3.5 w-3.5 text-accent flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    {cp ? cp.label : a.location_id.slice(0, 8) + "…"}
                  </div>
                  <div className="text-2xs text-text-muted truncate inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {humanizeRRule(a.rrule)}
                  </div>
                </div>
                {a.status === "archived" && (
                  <span className="badge-default text-2xs">已封存</span>
                )}
              </li>
            );
          })}
          {items.length > 5 && (
            <li className="text-2xs text-text-muted text-center pt-1">
              還有 {items.length - 5} 條，點「管理派發」查看全部
            </li>
          )}
        </ul>
      )}
    </fieldset>
  );
}

/** Verification method picker — single radio over the (verify_device,
 *  verify_face) tuple. Each task must verify identity through at least
 *  one method; the radio enforces that invariant in the admin UI by not
 *  exposing the (0,0) state at all. Legacy tasks stored as (0,0) round-
 *  trip back as "passkey" (arbitrary but stable default). */
type VerifyMethod = "passkey" | "face" | "both";
function tupleToMethod(verDev: boolean, verFace: boolean): VerifyMethod {
  if (verDev && verFace) return "both";
  if (verFace) return "face";
  return "passkey";          // covers (1,0) AND legacy (0,0)
}
function methodToTuple(m: VerifyMethod): { dev: boolean; face: boolean } {
  switch (m) {
    case "face":    return { dev: false, face: true };
    case "passkey": return { dev: true,  face: false };
    case "both":    return { dev: true,  face: true };
  }
}
function VerifyMethodRadio({ verDev, verFace, onChange }: {
  verDev: boolean; verFace: boolean;
  onChange: (verDev: boolean, verFace: boolean) => void;
}): React.ReactElement {
  const method = tupleToMethod(verDev, verFace);
  const options: { value: VerifyMethod; label: string; hint: string }[] = [
    { value: "passkey", label: "Passkey 驗證", hint: "巡邏員用已綁定的裝置完成通行金鑰驗證" },
    { value: "face",    label: "人臉辨識",     hint: "巡邏員依指示做出動作通過活體驗證，並比對已註冊的人臉樣本" },
    { value: "both",    label: "兩者都要",     hint: "依序完成 Passkey + 人臉，使用者自選順序" },
  ];
  return (
    <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-2">
      <legend className="text-sm font-semibold text-text-primary px-2">身分驗證方式</legend>
      <p className="text-2xs text-text-muted px-1 pb-1">巡邏員感應標籤 + 選路線後執行的身分驗證。此設定取「同地點上所有任務」的聯集做為實際要求的方法清單。</p>
      <div className="space-y-1.5">
        {options.map((o) => {
          const active = method === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => { const t = methodToTuple(o.value); onChange(t.dev, t.face); }}
              className={
                "w-full text-left flex items-start gap-3 rounded-md p-2.5 border transition-colors " +
                (active
                  ? "border-accent bg-accent-subtle"
                  : "border-border bg-bg-base hover:bg-bg-hover")
              }
            >
              <span className={
                "shrink-0 mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center " +
                (active ? "border-accent" : "border-text-muted")
              }>
                {active && <span className="w-2 h-2 rounded-full bg-accent" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className={"text-sm font-medium " + (active ? "text-accent" : "text-text-primary")}>{o.label}</p>
                <p className="text-2xs text-text-muted mt-0.5">{o.hint}</p>
              </div>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function Toggle({ label, sublabel, value, onChange }: {
  label: string; sublabel?: string; value: boolean; onChange: (v: boolean) => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className="w-full text-left flex items-center justify-between gap-3 hover:bg-bg-hover rounded-md p-2 -m-2"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {sublabel && <p className="text-2xs text-text-muted mt-0.5">{sublabel}</p>}
      </div>
      <span
        className={`shrink-0 w-10 h-5 rounded-full p-0.5 transition-colors ${value ? "bg-accent" : "bg-bg-base"}`}
        aria-pressed={value}
      >
        <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}

function TasksSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4" />
            <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
